import { TEAM_COLORS } from '../network/protocol';
import type { Building, Lake, ResNode, Snapshot, Unit } from '../network/protocol';
import { WORLD } from '../config';

// 2D canvas minimap: terrain dots for resources, team-colored dots/squares
// for units/buildings, and a white viewport rectangle. Click-through is
// handled by the Game controller via jumpToFraction().
export class Minimap {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private size: number;

  constructor(size = 176) {
    this.size = size;
    this.canvas = document.createElement('canvas');
    this.canvas.width = size;
    this.canvas.height = size;
    this.canvas.className =
      'rounded-md border border-white/20 cursor-pointer';
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    this.ctx = ctx;
  }

  private dot(x: number, z: number, color: string, r: number): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(
      (x / WORLD) * this.size - r / 2,
      (z / WORLD) * this.size - r / 2,
      r,
      r,
    );
  }

  draw(
    snap: Snapshot | null,
    view: { x: number; z: number; w: number },
  ): void {
    const { ctx, size } = this;
    ctx.fillStyle = '#17240f';
    ctx.fillRect(0, 0, size, size);
    if (snap) {
      for (const l of (snap.lk ?? []) as Lake[]) {
        // Same lobed shoreline as the sim, sampled as a filled path.
        ctx.fillStyle = '#2f6fb4';
        ctx.beginPath();
        for (let i = 0; i <= 24; i++) {
          const t = (i / 24) * Math.PI * 2;
          const e = 1 + l.a1 * Math.sin(2 * t + l.p1) + l.a2 * Math.sin(3 * t + l.p2);
          const px = ((l.x + l.rx * e * Math.cos(t)) / WORLD) * size;
          const py = ((l.z + l.rz * e * Math.sin(t)) / WORLD) * size;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.fill();
      }
      for (const n of snap.nd as ResNode[]) {
        this.dot(
          n.x, n.z,
          n.k === 'wood' ? '#2f6b2f' : n.k === 'gold' ? '#c9a227' : '#d06a9e',
          1.5,
        );
      }
      for (const u of snap.u as Unit[]) {
        this.dot(u.x, u.z, TEAM_COLORS[u.o] ?? '#fff', 2.4);
      }
      for (const b of snap.bd as Building[]) {
        this.dot(b.x, b.z, '#ffffff', 3);
        this.dot(b.x, b.z, TEAM_COLORS[b.o] ?? '#fff', 2);
      }
    }
    // Viewport rect.
    const vw = (view.w / WORLD) * size;
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineWidth = 1;
    ctx.strokeRect(
      (view.x / WORLD) * size - vw / 2,
      (view.z / WORLD) * size - vw / 2,
      vw,
      vw,
    );
  }

  // Convert a click pixel to a world target for the camera.
  toWorld(clientX: number, clientY: number): { x: number; z: number } {
    const rect = this.canvas.getBoundingClientRect();
    const fx = (clientX - rect.left) / rect.width;
    const fz = (clientY - rect.top) / rect.height;
    return { x: fx * WORLD, z: fz * WORLD };
  }
}
